import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, AlertTriangle, CreditCard, Mail, MessageCircle, Copy, ExternalLink, LogOut, Building2, ArrowRight, Phone, Banknote, Info, Loader2, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_STATUS_CONFIG = {
  TRIAL: { 
    label: 'Trial', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300', 
    icon: Clock,
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  ACTIVE: { 
    label: 'Attivo', 
    color: 'bg-green-100 text-green-800 border-green-300', 
    icon: CheckCircle,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  EXPIRED: { 
    label: 'Scaduto', 
    color: 'bg-red-100 text-red-800 border-red-300', 
    icon: XCircle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  SUSPENDED: { 
    label: 'Sospeso', 
    color: 'bg-gray-100 text-gray-800 border-gray-300', 
    icon: AlertTriangle,
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  }
};

export const WorkspaceStatus = () => {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('BASIC');
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/workspace/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStatus(await res.json());
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato negli appunti!`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Il server dice quali metodi sono davvero attivi: cosi' la carta non viene
  // proposta quando il gateway non e' configurato.
  const [metodiPagamento, setMetodiPagamento] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/subscriptions/payment-methods`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setMetodiPagamento(d))
      .catch(() => {});
  }, [token]);

  const cartaDisponibile = metodiPagamento
    ? !!metodiPagamento.methods.find((m) => m.code === 'CARD')?.available
    : false;
  const notaCarta = metodiPagamento
    ? metodiPagamento.methods.find((m) => m.code === 'CARD')?.note
    : '';

  const handlePurchase = async (payMethod) => {
    setPurchasing(true);
    try {
      // Build plan code based on selection and billing cycle
      const planCode = `${selectedPlan}_${billingCycle === 'yearly' ? 'Y' : 'M'}`;
      
      const response = await fetch(`${API_URL}/api/subscriptions/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_code: planCode,
          addon_codes: [],
          pay_method: payMethod,
          billing: {
            email: user?.email
          }
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPurchaseResult(data);
        
        if (payMethod === 'CARD' && data.checkout_url) {
          toast.success('Reindirizzamento al pagamento...');
          window.open(data.checkout_url, '_blank');
        } else if (payMethod === 'BANK') {
          toast.success('Ordine confermato! Segui le istruzioni per il bonifico.');
        }
      } else {
        toast.error(data.detail || 'Errore nella conferma dell\'ordine');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Errore di connessione. Riprova più tardi.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!status?.has_workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Nessun Workspace</h2>
            <p className="text-muted-foreground mb-4">
              Non hai ancora un workspace attivo. Contatta il supporto per attivare il tuo account.
            </p>
            <Button onClick={handleLogout} variant="outline">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = PLAN_STATUS_CONFIG[status.plan_status] || PLAN_STATUS_CONFIG.TRIAL;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <Building2 className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Stato Abbonamento</h1>
          <p className="text-muted-foreground">{status.workspace_name}</p>
        </div>

        {/* Status Card */}
        <Card className={`${statusConfig.borderColor} border-2`}>
          <CardHeader className={statusConfig.bgColor}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusIcon className={`h-8 w-8 ${
                  status.plan_status === 'ACTIVE' ? 'text-green-600' :
                  status.plan_status === 'EXPIRED' ? 'text-red-600' :
                  status.plan_status === 'SUSPENDED' ? 'text-gray-600' :
                  'text-yellow-600'
                }`} />
                <div>
                  <CardTitle className="text-xl">Piano {statusConfig.label}</CardTitle>
                  <CardDescription>
                    {status.plan_status === 'TRIAL' && `Scade il ${formatDate(status.trial_ends_at)}`}
                    {status.plan_status === 'ACTIVE' && 'Abbonamento attivo'}
                    {status.plan_status === 'EXPIRED' && 'Trial scaduta'}
                    {status.plan_status === 'SUSPENDED' && 'Account sospeso'}
                  </CardDescription>
                </div>
              </div>
              <Badge className={statusConfig.color}>
                {statusConfig.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Warning Alerts */}
            {status.plan_status === 'EXPIRED' && (
              <Alert className="mb-4 bg-red-50 border-red-200">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">Modalità Sola Lettura</AlertTitle>
                <AlertDescription className="text-red-700">
                  La tua trial è scaduta. Puoi visualizzare i dati esistenti ma non effettuare modifiche.
                  Attiva un abbonamento per ripristinare l&#39;accesso completo.
                </AlertDescription>
              </Alert>
            )}

            {status.plan_status === 'SUSPENDED' && (
              <Alert className="mb-4 bg-gray-100 border-gray-300">
                <AlertTriangle className="h-4 w-4 text-gray-600" />
                <AlertTitle className="text-gray-800">Accesso Sospeso</AlertTitle>
                <AlertDescription className="text-gray-700">
                  {status.lock_reason ? (
                    <span>Motivo: {status.lock_reason}</span>
                  ) : (
                    <span>Il tuo account è stato sospeso. Contatta il supporto per maggiori informazioni.</span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Trial Countdown */}
            {status.plan_status === 'TRIAL' && status.days_remaining !== null && (
              <div className={`p-4 rounded-lg mb-4 ${
                status.days_remaining <= 3 ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center gap-3">
                  <Clock className={`h-8 w-8 ${status.days_remaining <= 3 ? 'text-red-500' : 'text-yellow-500'}`} />
                  <div>
                    <p className="font-medium text-lg">
                      {status.days_remaining === 0 
                        ? 'La trial scade oggi!' 
                        : `${status.days_remaining} giorni rimasti`
                      }
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Attiva l&#39;abbonamento per continuare a usare tutte le funzionalità
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Key Dates */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {status.trial_started_at && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Inizio Trial</p>
                  <p className="font-medium">{formatDate(status.trial_started_at)}</p>
                </div>
              )}
              {status.trial_ends_at && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Fine Trial</p>
                  <p className="font-medium">{formatDate(status.trial_ends_at)}</p>
                </div>
              )}
              {status.subscription_started_at && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Abbonamento dal</p>
                  <p className="font-medium">{formatDate(status.subscription_started_at)}</p>
                </div>
              )}
              {status.next_due_at && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Prossimo rinnovo</p>
                  <p className="font-medium">{formatDate(status.next_due_at)}</p>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            {/* Upgrade CTA */}
            {status.plan_status !== 'ACTIVE' && (
              <div className="space-y-4">
                <Button 
                  className="w-full h-12 text-lg"
                  onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  {showPaymentDetails ? 'Nascondi Opzioni' : 'Passa ad Abbonamento'}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>

                {/* Payment Instructions */}
                {showPaymentDetails && status.billing_instructions && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Banknote className="h-5 w-5" />
                        Scegli il tuo Piano
                      </CardTitle>
                      {status.billing_instructions.vat_note && (
                        <p className="text-xs text-orange-600 font-medium mt-1">
                          {status.billing_instructions.vat_note}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Billing Cycle Toggle */}
                      <div className="flex justify-center">
                        <div className="bg-gray-100 p-1 rounded-lg inline-flex">
                          <button
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                              billingCycle === 'monthly'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Mensile
                          </button>
                          <button
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                              billingCycle === 'yearly'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Annuale
                            <span className="ml-2 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">-17%</span>
                          </button>
                        </div>
                      </div>

                      {/* Plan Selection */}
                      <div className="grid grid-cols-2 gap-3">
                        {status.billing_instructions.plans && Object.entries(status.billing_instructions.plans).map(([code, plan]) => (
                          <div
                            key={code}
                            onClick={() => setSelectedPlan(code)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedPlan === code 
                                ? 'border-blue-600 bg-blue-100' 
                                : 'border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-bold text-lg">{plan.name}</h4>
                              {selectedPlan === code && (
                                <CheckCircle className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{plan.description}</p>
                            {billingCycle === 'monthly' ? (
                              <>
                                <p className="text-2xl font-bold">€{plan.monthly_price}</p>
                                <p className="text-xs text-muted-foreground">+ IVA/mese</p>
                              </>
                            ) : (
                              <>
                                <p className="text-2xl font-bold text-green-600">€{Math.round(plan.yearly_price / 12)}</p>
                                <p className="text-xs text-muted-foreground">+ IVA/mese (fatturato annualmente)</p>
                                <p className="text-sm text-green-600 mt-1">
                                  Totale: €{plan.yearly_price} + IVA/anno
                                </p>
                                <p className="text-xs text-green-700">
                                  Risparmi €{(plan.monthly_price * 12 - plan.yearly_price).toFixed(0)}!
                                </p>
                              </>
                            )}
                            <div className="mt-3 pt-3 border-t text-xs text-muted-foreground space-y-1">
                              <p>• {plan.limits.condominiums} condomini</p>
                              <p>• {plan.limits.units.toLocaleString()} unità</p>
                              <p>• {plan.limits.collaborators} collaboratori</p>
                              <p>• {plan.limits.storage_gb} GB storage</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Selected Plan Payment */}
                      {selectedPlan && status.billing_instructions.plans?.[selectedPlan] && (
                        <>
                          {/* Payment Summary */}
                          <div className="p-4 bg-slate-100 rounded-lg">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm text-muted-foreground">Riepilogo Ordine</p>
                                <p className="font-bold text-lg">
                                  Piano {status.billing_instructions.plans[selectedPlan].name} - {billingCycle === 'monthly' ? 'Mensile' : 'Annuale'}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold">
                                  €{billingCycle === 'monthly' 
                                    ? status.billing_instructions.plans[selectedPlan].monthly_price 
                                    : status.billing_instructions.plans[selectedPlan].yearly_price}
                                </p>
                                <p className="text-xs text-muted-foreground">+ IVA</p>
                              </div>
                            </div>
                          </div>

                          {/* Pagamento con carta - Stripe */}
                          <div
                            className={`p-4 rounded-lg text-white ${
                              cartaDisponibile
                                ? 'bg-gradient-to-r from-indigo-500 to-purple-600'
                                : 'bg-stone-500'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="font-semibold flex items-center gap-2">
                                  <CreditCard className="h-5 w-5" />
                                  Paga con carta di credito o debito
                                </p>
                                <p className="text-sm text-white/85 mt-1">
                                  {cartaDisponibile
                                    ? 'Pagamento sicuro con Stripe: l’abbonamento si attiva subito.'
                                    : notaCarta || 'Metodo non ancora attivo su questo server.'}
                                </p>
                              </div>
                              <Button
                                className="bg-white text-indigo-600 hover:bg-white/90 shrink-0"
                                onClick={() => handlePurchase('CARD')}
                                disabled={purchasing || !cartaDisponibile}
                                title={cartaDisponibile ? '' : notaCarta}
                              >
                                {purchasing ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                )}
                                {cartaDisponibile ? 'Paga con carta' : 'Non disponibile'}
                              </Button>
                            </div>
                          </div>

                          {/* Bank Transfer Section */}
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                              <Banknote className="h-4 w-4" />
                              Oppure paga con Bonifico Bancario
                            </p>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground text-sm">Banca:</span>
                                <span className="font-medium">{status.billing_instructions.bank_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground text-sm">Intestatario:</span>
                                <span className="font-medium text-right text-sm">{status.billing_instructions.account_holder}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground text-sm">IBAN:</span>
                                <div className="flex items-center gap-2">
                                  <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                                    {status.billing_instructions.iban}
                                  </code>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => copyToClipboard(status.billing_instructions.iban, 'IBAN')}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-3 bg-yellow-100 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                  Causale Piano {status.billing_instructions.plans[selectedPlan].name} {billingCycle === 'monthly' ? 'MENSILE' : 'ANNUALE'} (importante!):
                                </p>
                                <p className="font-mono text-sm">
                                  {purchaseResult?.causale || `${status.billing_instructions.plans[selectedPlan].causale} - ${billingCycle === 'monthly' ? 'MENSILE' : 'ANNUALE'}`}
                                </p>
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => copyToClipboard(
                                  purchaseResult?.causale || `${status.billing_instructions.plans[selectedPlan].causale} - ${billingCycle === 'monthly' ? 'MENSILE' : 'ANNUALE'}`, 
                                  'Causale'
                                )}
                              >
                                <Copy className="h-4 w-4 mr-1" />
                                Copia
                              </Button>
                            </div>
                          </div>

                          {/* Confirm Bank Transfer Order Button */}
                          <Button 
                            className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handlePurchase('BANK')}
                            disabled={purchasing}
                          >
                            {purchasing ? (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Elaborazione...
                              </>
                            ) : (
                              <>
                                <ShoppingCart className="h-5 w-5 mr-2" />
                                Conferma Ordine Bonifico
                              </>
                            )}
                          </Button>

                          {/* Purchase Result Message */}
                          {purchaseResult && purchaseResult.pay_method === 'BANK' && (
                            <Alert className="bg-green-100 border-green-300">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <AlertTitle className="text-green-800">Ordine Confermato!</AlertTitle>
                              <AlertDescription className="text-green-700 space-y-2">
                                <p><strong>ID Ordine:</strong> {purchaseResult.order_id}</p>
                                <p><strong>Importo:</strong> {purchaseResult.amount?.formatted?.gross} (IVA inclusa)</p>
                                <p><strong>Causale da utilizzare:</strong> <code className="bg-white px-2 py-1 rounded">{purchaseResult.causale}</code></p>
                                <p className="text-sm mt-2">{purchaseResult.instructions?.it}</p>
                              </AlertDescription>
                            </Alert>
                          )}
                        </>
                      )}

                      <Alert className="bg-blue-100 border-blue-300">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800 text-sm">
                          Dopo il pagamento, contattaci su WhatsApp o email con la ricevuta.
                          Il tuo account verrà attivato entro 24 ore lavorative.
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>
                )}

                {/* Upgrade Options - Only for ACTIVE plans */}
                {status.plan_status === 'ACTIVE' && status.billing_instructions?.upgrades && (
                  <>
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowUpgrade(!showUpgrade)}
                    >
                      {showUpgrade ? 'Nascondi Upgrade' : 'Pacchetti Upgrade'}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>

                    {showUpgrade && (
                      <Card className="bg-green-50 border-green-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">Pacchetti Upgrade</CardTitle>
                          <p className="text-xs text-orange-600 font-medium">
                            {status.billing_instructions.vat_note}
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {Object.entries(status.billing_instructions.upgrades).map(([code, upgrade]) => (
                            <div key={code} className="p-4 bg-white rounded-lg border">
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <h4 className="font-bold">{upgrade.name}</h4>
                                  <p className="text-lg font-bold text-green-600">
                                    €{upgrade.monthly_price} + IVA/mese
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    o €{upgrade.yearly_price} + IVA/anno
                                  </p>
                                </div>
                                <Button 
                                  size="sm"
                                  onClick={() => window.open(status.billing_instructions.card_payment_url, '_blank')}
                                >
                                  Acquista
                                </Button>
                              </div>
                              <ul className="text-sm text-muted-foreground space-y-1">
                                {upgrade.includes.map((item, idx) => (
                                  <li key={idx}>• {item}</li>
                                ))}
                              </ul>
                              <div className="mt-3 p-2 bg-yellow-50 rounded text-xs">
                                <span className="font-medium">Causale: </span>
                                <code className="font-mono">{upgrade.causale}</code>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="ml-2 h-6 px-2"
                                  onClick={() => copyToClipboard(upgrade.causale, 'Causale Upgrade')}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Upgrade Options for ACTIVE plans */}
            {status.plan_status === 'ACTIVE' && status.billing_instructions?.upgrades && (
              <div className="space-y-4">
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowUpgrade(!showUpgrade)}
                >
                  {showUpgrade ? 'Nascondi Upgrade' : 'Acquista Pacchetto Upgrade'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>

                {showUpgrade && (
                  <Card className="bg-green-50 border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Pacchetti Upgrade Disponibili</CardTitle>
                      <p className="text-xs text-orange-600 font-medium">
                        {status.billing_instructions.vat_note}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(status.billing_instructions.upgrades).map(([code, upgrade]) => (
                        <div key={code} className="p-4 bg-white rounded-lg border">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-bold">{upgrade.name}</h4>
                              <p className="text-lg font-bold text-green-600">
                                €{upgrade.monthly_price} + IVA/mese
                              </p>
                              <p className="text-xs text-muted-foreground">
                                o €{upgrade.yearly_price} + IVA/anno
                              </p>
                            </div>
                            <Button 
                              size="sm"
                              onClick={() => window.open(status.billing_instructions.card_payment_url, '_blank')}
                            >
                              Acquista
                            </Button>
                          </div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {upgrade.includes.map((item, idx) => (
                              <li key={idx}>• {item}</li>
                            ))}
                          </ul>
                          <div className="mt-3 p-2 bg-yellow-50 rounded text-xs">
                            <span className="font-medium">Causale bonifico: </span>
                            <code className="font-mono">{upgrade.causale}</code>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="ml-2 h-6 px-2"
                              onClick={() => copyToClipboard(upgrade.causale, 'Causale Upgrade')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}

                      {/* Bank info for upgrades */}
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm font-medium mb-2">Coordinate per bonifico:</p>
                        <div className="text-xs space-y-1 text-muted-foreground">
                          <p><span className="font-medium">Banca:</span> {status.billing_instructions.bank_name}</p>
                          <p><span className="font-medium">IBAN:</span> {status.billing_instructions.iban}</p>
                          <p><span className="font-medium">Intestatario:</span> {status.billing_instructions.account_holder}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Hai bisogno di aiuto?</CardTitle>
            <CardDescription>Contatta il nostro team di supporto</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col items-start text-left"
                onClick={() => window.open(`mailto:${status.support?.email}?subject=Assistenza ${status.workspace_name}`, '_blank')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">Email</span>
                </div>
                <span className="text-sm text-muted-foreground">{status.support?.email}</span>
              </Button>

              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col items-start text-left bg-green-50 hover:bg-green-100 border-green-200"
                onClick={() => window.open(status.support?.whatsapp_link, '_blank')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium">WhatsApp</span>
                  <ExternalLink className="h-3 w-3" />
                </div>
                <span className="text-sm text-muted-foreground">Messaggio diretto</span>
              </Button>
            </div>

            {status.support?.phone && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4" />
                <span>{status.support.phone}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-center gap-4">
          {status.plan_status !== 'SUSPENDED' && (
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Torna alla Dashboard
            </Button>
          )}
          <Button variant="ghost" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-stone-50 py-6 px-6 md:px-8 lg:px-12 mt-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src="/assets/artego-symbol.png"
                alt="Artego SRLS"
                className="h-10 w-auto"
              />
              <div className="text-sm text-stone-600">
                <p className="font-semibold text-stone-800">Artego SRLS</p>
                <p>Via M.se di Villabianca, 114 - 90143 Palermo</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-stone-500">
              <a href="https://www.artegosrls.it" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 font-medium">
                www.artegosrls.it
              </a>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors" aria-label="Facebook">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors" aria-label="Instagram">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://wa.me/393520498295" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-green-600 transition-colors" aria-label="WhatsApp">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a href="https://www.youtube.com/@artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-600 transition-colors" aria-label="YouTube">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-stone-200 text-center text-xs text-stone-500">
            <p>© 2026 Artego SRLS. Tutti i diritti riservati. P.IVA 07221580827</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WorkspaceStatus;
